// @flow
import _ from 'lodash';
import moment from 'moment';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';

// Saved steps carry their uuid; a step added in the modal and not saved yet has a numeric id.
type RentStepId = string | number;

type RentStepRow = {
  id: RentStepId,
  idx: number,
  startDate: ?string,
  endDate: ?string,
  amount?: ?number,
};

/**
 * Removes a rent step from the modal's list and re-indexes the rest. The prior step is closed the
 * day before the next remaining step starts -- the same rule onRentStepChange applies when a step's
 * date moves -- and stays open only when the deleted step was the last one. Opening it
 * unconditionally left two open steps whenever a later step existed (ENG-33092).
 *
 * Derived from the next step rather than copied from the deleted step's own end date: a saved step
 * with no end date is data that exists today, and copying that null reopened the prior step and
 * reproduced the same overlap. On healthy data the two are identical.
 */
export const removeRentStep = (
  rentSteps: Array<RentStepRow>,
  { id, idx }: { id: RentStepId, idx: number },
): Array<RentStepRow> => {
  const stepToDelete = rentSteps.find((step) => step.id === id);
  const priorStep = rentSteps.find((step) => step.idx === idx - 1);
  if (!stepToDelete || !priorStep) return rentSteps;

  // A step added in the modal and not dated yet carries startDate: null, and moment(null/undefined)
  // is *now* rather than invalid -- without the truthiness check that would silently close the prior
  // step yesterday. No next step, or no date on it, means the prior step is the open one.
  const nextStep = rentSteps.find((step) => step.idx === idx + 1);
  const updatedPriorStep = {
    ...priorStep,
    endDate: nextStep?.startDate
      ? formatDateDB(
          moment(nextStep.startDate).clone().subtract(1, 'days'),
          false,
        )
      : null,
  };

  return _.orderBy(
    [
      ...rentSteps.filter((step) => step.id !== id && step.id !== priorStep.id),
      updatedPriorStep,
    ],
    'idx',
    'asc',
  ).map((step, newIdx) => ({ ...step, idx: newIdx }));
};
